import api from './api';

export interface UploadSessionFileInfo {
  id: number;
  dateiname: string;
  dateigroesse: number | null;
  hochgeladen_am: string;
}

export interface UploadSessionListItem {
  id: number;
  token: string;
  secret: string;
  beschreibung: string;
  gueltig_bis: string;
  status: string;
  erstellt_am: string;
  erstellt_von: { id: number; vorname: string; nachname: string } | null;
  datei_anzahl: number;
}

export interface UploadSessionDetail {
  id: number;
  token: string;
  secret: string;
  beschreibung: string;
  gueltig_bis: string;
  status: string;
  erstellt_am: string;
  erstellt_von: { id: number; vorname: string; nachname: string } | null;
  dateien: UploadSessionFileInfo[];
}

export interface UploadSessionCreate {
  beschreibung: string;
  gueltig_bis: string;
}

export interface UploadSessionUpdate {
  beschreibung?: string;
  gueltig_bis?: string;
  secret?: string;
}

export interface PublicSessionInfo {
  beschreibung: string;
  status: string;
  gueltig_bis: string;
}

export interface PublicUploadResult {
  dateiname: string;
  dateigroesse: number | null;
  hochgeladen_am: string;
}

export const uploadSessionsService = {
  list: async (): Promise<UploadSessionListItem[]> => {
    const response = await api.get('/upload-sessions/');
    return response.data;
  },

  create: async (data: UploadSessionCreate): Promise<UploadSessionDetail> => {
    const response = await api.post('/upload-sessions/', data);
    return response.data;
  },

  get: async (id: number): Promise<UploadSessionDetail> => {
    const response = await api.get(`/upload-sessions/${id}`);
    return response.data;
  },

  update: async (id: number, data: UploadSessionUpdate): Promise<UploadSessionDetail> => {
    const response = await api.put(`/upload-sessions/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/upload-sessions/${id}`);
  },

  downloadFile: (sessionId: number, fileId: number): string => {
    return `${api.defaults.baseURL}/upload-sessions/${sessionId}/files/${fileId}/download`;
  },

  downloadAllZip: (sessionId: number): string => {
    return `${api.defaults.baseURL}/upload-sessions/${sessionId}/download-zip`;
  },

  getPublicSession: async (token: string): Promise<PublicSessionInfo> => {
    const response = await api.get(`/upload-sessions/public/${token}`);
    return response.data;
  },

  verifySecret: async (token: string, secret: string): Promise<boolean> => {
    const response = await api.post(`/upload-sessions/public/${token}/verify`, { secret });
    return response.data.valid;
  },

  uploadFiles: async (
    token: string,
    secret: string,
    files: File[],
    onUploadProgress?: (percent: number) => void,
  ): Promise<PublicUploadResult[]> => {
    // Eine Datei pro Request, damit jede bis zum Nginx-Limit (20 GiB) durchkommt.
    const totalBytes = files.reduce((sum, file) => sum + (file.size || 0), 0);
    let completedBytes = 0;
    const results: PublicUploadResult[] = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append('files', file);
      const response = await api.post(`/upload-sessions/public/${token}/upload`, formData, {
        headers: { 'X-Upload-Secret': secret },
        timeout: 0,
        onUploadProgress: (e) => {
          if (!onUploadProgress || totalBytes <= 0) return;
          const loaded = e.loaded || 0;
          const percent = Math.round(((completedBytes + loaded) / totalBytes) * 100);
          onUploadProgress(Math.min(100, percent));
        },
      });
      results.push(...response.data);
      completedBytes += file.size || 0;
    }
    return results;
  },

  listPublicFiles: async (token: string, secret: string): Promise<UploadSessionFileInfo[]> => {
    const response = await api.get(`/upload-sessions/public/${token}/files`, {
      headers: { 'X-Upload-Secret': secret },
    });
    return response.data;
  },
};
